import { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
import { readData } from '@/firebase/database';

interface Testimonial {
  name: string;
  role: string;
  text: string;
  avatar: string;
  image?: string;
  rating: number;
}

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [current, setCurrent] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  useEffect(() => {
    readData('testimonials').then((data) => {
      if (data) setTestimonials(Object.values(data) as Testimonial[]);
    });
  }, []);

  useEffect(() => {
    if (testimonials.length === 0) return;
    const interval = setInterval(() => setCurrent((p) => (p + 1) % testimonials.length), 5000);
    return () => clearInterval(interval);
  }, [testimonials]);

  const renderAvatar = (t: Testimonial) => {
    if (t.image) return <img src={t.image} alt={t.name} className="w-full h-full object-cover rounded-full" />;
    return <span className="text-xs sm:text-sm font-bold">{t.avatar}</span>;
  };

  return (
    <section id="testimonials" className="py-16 sm:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6" ref={ref}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="text-center mb-10 sm:mb-16">
          <p className="section-tag text-xs">What clients say</p>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold">
            Testi<span className="gradient-text">monials</span>
          </h2>
          <div className="w-20 h-0.5 mx-auto mt-4 bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
        </motion.div>

        {testimonials.length > 0 && (
          <div className="max-w-3xl mx-auto">
            <motion.div key={current} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="glass neon-border rounded-2xl p-5 sm:p-8 text-center relative">
              <Quote size={28} className="text-primary/20 absolute top-4 left-4 sm:top-6 sm:left-6" />
              <div className="flex justify-center gap-1 mb-4 sm:mb-6">
                {Array.from({ length: testimonials[current].rating }).map((_, i) => (
                  <Star key={i} size={14} className="fill-primary text-primary" />
                ))}
              </div>
              <p className="text-foreground text-sm sm:text-lg leading-relaxed mb-6 sm:mb-8 relative z-10">
                "{testimonials[current].text}"
              </p>
              <div className="flex items-center justify-center gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-muted border border-border flex items-center justify-center overflow-hidden">
                  {renderAvatar(testimonials[current])}
                </div>
                <div className="text-left">
                  <p className="font-display font-bold text-sm sm:text-base">{testimonials[current].name}</p>
                  <p className="text-muted-foreground text-xs sm:text-sm font-mono">{testimonials[current].role}</p>
                </div>
              </div>
            </motion.div>

            <div className="flex justify-center gap-2 mt-4 sm:mt-6">
              {testimonials.map((_, i) => (
                <button key={i} onClick={() => setCurrent(i)}
                  className={`rounded-full transition-all duration-300 ${i === current ? 'w-6 sm:w-8 h-1.5 sm:h-2 bg-primary' : 'w-1.5 sm:w-2 h-1.5 sm:h-2 bg-muted-foreground/40 hover:bg-muted-foreground'}`} />
              ))}
            </div>
          </div>
        )}

        <div className="hidden lg:grid grid-cols-3 gap-6 mt-16">
          {testimonials.map((t, i) => (
            <motion.div key={t.name} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: i * 0.15 }}
              className="glass border border-border rounded-2xl p-6 card-hover">
              <div className="flex gap-1 mb-3">
                {Array.from({ length: t.rating }).map((_, j) => <Star key={j} size={14} className="fill-primary text-primary" />)}
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">"{t.text}"</p>
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-muted border border-border flex items-center justify-center text-xs font-bold overflow-hidden">{renderAvatar(t)}</div>
                <div>
                  <p className="font-semibold text-sm">{t.name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{t.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
